<template>
  <div class="view-gabarit">
    <div class="view-content">
      <div class="view-row">
        <div class="site-box">
          <div class="image-box">
            <div class="image-holder" :style="{ backgroundImage: `url(${siteImage})` }"></div>
          </div>
          <div class="details-box">
            <div class="poi-holder" :style="{ backgroundImage: `url(${sitePOIImage})` }"></div>
            <div class="details-content">
              <p class="title">{{ site.name }}</p>
              <p class="address">{{ site.address }}</p>
            </div>
          </div>
          <div class="details-box">
            <div class="row-cell">
              <span class="row-head">Proportion</span>
            </div>
            <div class="row-cell">
              <div class="row-body">
                <AnimatedValue :value="(collaborators.length / screenData.filteredCollaboratorsCount) * 100" />
                <small>%</small>
              </div>
              <div class="row-body">
                <AnimatedValue :value="collaborators.length" /> <small><i class="icon-people"></i></small>
              </div>
            </div>
          </div>
          <div class="details-box">
            <div class="row-cell">
              <span class="row-head">Temps de trajet moyen</span>
            </div>
            <div class="row-cell">
              <div class="row-body"><AnimatedValue :value="averageTime.value" /> <small>min</small></div>
              <div class="row-body">
                <i :class="`icon-${averageTime.trend}`"></i><AnimatedValue :value="averageTime.diff" />
                <small>min</small>
              </div>
            </div>
          </div>
        </div>
        <AnalysisSection class="grown">
          <template #header>
            <h5>{{ $t('report.outlierByLocalisation') }}</h5>
          </template>
          <template #body>
            <CollaboratorsOrigins :data="computedData" />
          </template>
        </AnalysisSection>
      </div>
      <div class="view-row">
        <AnalysisSection
          v-if="
            !(
              collaboratorsByContractType.length > 1 &&
              collaboratorsByContractType.reduce((acc, val) => {
                return acc + val.label;
              }, '') === ''
            )
          "
        >
          <template #header>
            <h5>{{ $t('report.outlierByContractType') }}</h5>
          </template>
          <template #body>
            <LabelPieChart :entries="collaboratorsByContractType" />
          </template>
        </AnalysisSection>
        <AnalysisSection
          v-if="
            !(
              collaboratorsByContractType.length > 1 &&
              collaboratorsByService.reduce((acc, val) => {
                return acc + val.label;
              }, '') === ''
            )
          "
        >
          <template #header>
            <h5>{{ $t('report.outlierByService') }}</h5>
          </template>
          <template #body>
            <LabelPieChart :entries="collaboratorsByService" />
          </template>
        </AnalysisSection>
        <AnalysisSection
          v-if="
            !(
              collaboratorsByContractType.length > 1 &&
              collaboratorsBySeniority.reduce((acc, val) => {
                return acc + val.label;
              }, '') === ''
            )
          "
        >
          <template #header>
            <h5>{{ $t('report.outlierBySeniority') }}</h5>
          </template>
          <template #body>
            <LabelPieChart :entries="collaboratorsBySeniority" />
          </template>
        </AnalysisSection>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import assetProvider from '@/pure/assetProvider';
import AnalysisSection from '@/components/Map/Analysis/AnalysisSection';
import CollaboratorsOrigins from '@/components/Analysis/Synthesis/CollaboratorOrigins';
import LabelPieChart from '@/components/Chart/LabelPie';
import AnimatedValue from '@/components/AnimatedValue';

export default {
  components: {
    AnalysisSection,
    CollaboratorsOrigins,
    LabelPieChart,
    AnimatedValue,
  },

  props: ['params'],

  computed: {
    ...mapGetters({
      screenData: 'screenData',
    }),

    site() {
      return this.screenData.sites.find(item => item.id === this.params.id);
    },

    siteImage() {
      return this.site.picture_path || assetProvider.placeHolderSite;
    },

    sitePOIImage() {
      return assetProvider[`poi${this.site.index + 1}`];
    },

    computedData() {
      const { details } = this.site;
      return {
        collaboratorsByRegion: details.sensibleCollaboratorsByRegion,
        filteredCollaboratorsCount: details.sensibleCaseCollaborators.length,
      };
    },

    averageTime() {
      const { averageTime, averageTimeDiff } = this.site.details.impact.sensibleCaseDecay;
      return {
        trend: averageTimeDiff > 0 ? 'up' : 'down',
        diff: averageTimeDiff,
        value: averageTime,
      };
    },

    collaborators() {
      return this.site.details.sensibleCaseCollaborators;
    },

    collaboratorsByContractType() {
      return this.getSummaryPieByDetails('contractType');
    },

    collaboratorsByService() {
      return this.getSummaryPieByDetails('service');
    },

    collaboratorsBySeniority() {
      return this.getSummaryPieByDetails('seniority');
    },
  },

  methods: {
    getSummaryPieByDetails(key) {
      const summary = this.collaborators.reduce((acc, person) => {
        const currentKey = person.details[key];
        const currentGroup = acc.find(item => item.label === currentKey);
        if (!currentGroup) {
          acc.push({
            label: currentKey,
            count: 1,
          });
        } else {
          currentGroup.count++;
        }
        return acc;
      }, []);

      let i = 0;
      let count = 0;
      let percentage = 0;
      let newSummary = [];
      for (i = 0; i < summary.length; i++) {
        summary[i].percentage = summary[i].count / this.collaborators.length;

        if (summary[i].percentage < 0.06) {
          count += summary[i].count;
          percentage += summary[i].percentage;
        } else {
          newSummary.push(summary[i]);
        }
      }

      if (count > 0) {
        newSummary.push({
          label: this.$t('other'),
          count: count,
          percentage: percentage,
        });
      }

      return newSummary.sort((a, b) => b.label - a.label);
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';
.view-gabarit {
  width: 1200px;
  margin-left: -30px;
  margin-right: -30px;
  margin-bottom: -19px;
  margin-top: -59px;
  padding-left: 25px;
  padding-right: 25px;
  padding-bottom: 19px;
  padding-top: 59px;
  background-color: #f4f4f4;
  .view-content {
  }
}
.view-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}
.site-box {
  margin-right: 2.60869566%;
  background-color: #ffffff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 29.65217391%;
  .image-box {
    width: 272px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 22px;
    .image-holder {
      padding-bottom: 76.0882352941%;
      height: 0;
      background-position: 50% 50%;
      background-repeat: no-repeat;
      background-size: cover;
      box-shadow: 0 2px 12px -4px rgba(0, 0, 0, 0.6);
      border-radius: 6px;
    }
  }
  .details-box {
    margin-left: 25px;
    margin-right: 25px;
    padding-top: 18px;
    padding-bottom: 18px;
    display: flex;
    align-items: center;
    & + .details-box {
      border-top: 1px solid #d8d8d8;
    }
    .details-content {
      flex: 1;
    }
    p {
      color: #3c3c3b;
      margin: 0;
      &.title {
        font-size: 20px;
        font-weight: 800;
      }
      &.address {
        font-size: 14px;
        margin-top: 0.5em;
      }
    }
  }
  .poi-holder {
    width: 30px;
    height: 50px;
    background-position: 50% 50%;
    background-repeat: no-repeat;
    background-size: contain;
    margin-right: 16px;
  }
  .row-cell {
    display: flex;
    align-items: center;
    &:first-child {
      flex: 0.7;
    }
    &:nth-child(2) {
      flex: 1;
    }
    .row-head {
      color: #9b9b9b;
      font-size: 14px;
    }
    .row-body {
      flex: 1;
      font-size: 20px;
      color: #000;
    }
  }
}
.analysis-section :deep(.heading) {
  background-color: #f7f7f7;
  height: 64px;
  padding-left: 28px;
}
.analysis-section {
  background-color: #ffffff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  flex-grow: 1;
  max-width: 48.69565217%;

  margin-top: 31px;

  &.grown {
    max-width: initial;
  }
  h5 {
    font-size: 18px;
    font-weight: 600;
  }
}
</style>
